import {ErrorCode} from '@sollapay/enums';
import {normalizeEmail, validateEmail} from '@sollapay/utils';

import {getErrorCode, getErrorDetails} from '@/utils';

import type {TrustAccountMemberIneligibleReason, TrustAccountMemberRole} from '@sollapay/enums';
import type {TrustAccountMemberCandidateDTO} from '@sollapay/types';

export type ShareRefusal = 'invalid' | 'existing' | 'firm';

export type ShareChipProblem = ShareRefusal | TrustAccountMemberIneligibleReason;

/** A firm user joins as a member, anyone else is invited as a guest; a problem blocks the send. */
export type ShareChip =
  | {email: string; kind: 'member'; person: TrustAccountMemberCandidateDTO}
  | {email: string; kind: 'guest'}
  | {email: string; kind: 'problem'; problem: ShareChipProblem; name?: string};

const LIST_SEPARATORS = /[,;]/;
const WHITESPACE = /\s+/;

const SHARE_REFUSALS: readonly ShareChipProblem[] = ['invalid', 'existing', 'firm'];

const REFUSAL_PROBLEM: Partial<Record<string, ShareRefusal>> = {
  [ErrorCode.GUEST_EMAIL_IS_FIRM_MEMBER]: 'firm',
  [ErrorCode.GUEST_ALREADY_INVITED]: 'existing'
};

/**
 * Commas and semicolons always end an entry. Whitespace ends one only once it holds an `@`, so a
 * name search can have spaces in it.
 */
export function splitEmailDraft(draft: string): {complete: string[]; rest: string} {
  const pieces = draft.split(LIST_SEPARATORS);
  const last = (pieces.pop() ?? '').trimStart();
  const complete = pieces.flatMap(splitDraftPiece);
  if (!looksLikeEmail(last)) return {complete, rest: last};
  const words = last.split(WHITESPACE);
  const rest = words.pop() ?? '';
  return {complete: [...complete, ...words.filter(Boolean)], rest};
}

export function looksLikeEmail(draft: string): boolean {
  return draft.includes('@');
}

/** Appends the incoming addresses, normalized, skipping any already listed. */
export function addChipEmails(emails: readonly string[], incoming: readonly string[]): string[] {
  const next = [...emails];
  for (const raw of incoming) {
    const email = normalizeEmail(raw);
    if (email && !next.includes(email)) next.push(email);
  }
  return next;
}

export function isValidShareEmail(email: string): boolean {
  return !!email && !validateEmail(email);
}

/** A client-side hint only — the server stays the authority on who may be added or invited. */
export function classifyShareEntry(params: {
  email: string;
  role: TrustAccountMemberRole;
  candidatesByEmail: ReadonlyMap<string, TrustAccountMemberCandidateDTO>;
  accessEmails: ReadonlySet<string>;
}): ShareChip {
  const {role, candidatesByEmail, accessEmails} = params;
  const email = normalizeEmail(params.email);
  if (!isValidShareEmail(email)) return {email, kind: 'problem', problem: 'invalid'};
  if (accessEmails.has(email)) return {email, kind: 'problem', problem: 'existing'};

  const person = candidatesByEmail.get(email);
  if (!person) return {email, kind: 'guest'};
  const ineligible = person.ineligibleReasons[role];
  return ineligible
    ? {email, kind: 'problem', problem: ineligible, name: person.name}
    : {email, kind: 'member', person};
}

/** The `trustAccounts` key of a problem's reason; a firm user's ineligibility reuses the team's copy. */
export function getShareProblemKey(problem: ShareChipProblem): string {
  return SHARE_REFUSALS.includes(problem)
    ? `share.add.reasons.${problem}`
    : `members.ineligible.${problem}`;
}

/** Each refused email's problem from a `GUEST_INVITE_REFUSED` response; `null` when there's none to mark. */
export function getShareRefusals(error: unknown): Map<string, ShareRefusal> | null {
  if (getErrorCode(error) !== ErrorCode.GUEST_INVITE_REFUSED) return null;
  const details = getErrorDetails(error) as
    | {refusals?: {email: string; code: string}[]}
    | undefined;
  const refusals = details?.refusals ?? [];
  if (!refusals.length) return null;
  return new Map(
    refusals.map(({email, code}) => [normalizeEmail(email), REFUSAL_PROBLEM[code] ?? 'invalid'])
  );
}

function splitDraftPiece(piece: string): string[] {
  const entry = piece.trim();
  if (!entry) return [];
  return looksLikeEmail(entry) ? entry.split(WHITESPACE) : [entry];
}
